import React from "react";

import { useAppI18n } from "../hooks/useAppI18n";
import { useOneClickVideo } from "../hooks/useOneClickVideo";

import { AIVideoButton } from "./AIVideoButton";
import { RecordingFeature } from "./RecordingFeature";
import "./IdeaWeaveCanvas.scss";

interface IdeaWeaveCanvasHeaderProps {
  onExport: () => void;
  onProjectToggle: () => void;
  isProjectShelfOpen: boolean;
}

export const IdeaWeaveCanvasHeader: React.FC<IdeaWeaveCanvasHeaderProps> = ({
  onExport,
  onProjectToggle,
  isProjectShelfOpen,
}) => {
  const { t } = useAppI18n();
  const video = useOneClickVideo();

  return (
    <>
      {/* Left side - Project Shelf toggle */}
      <div className="ideaweave-canvas-header__left">
        <button
          type="button"
          className="ideaweave-project-toggle"
          onClick={onProjectToggle}
          aria-expanded={isProjectShelfOpen}
          aria-controls="ideaweave-project-panel"
          title={t("canvas.projectShelf")}
        >
          <span className="material-symbols-outlined">view_sidebar</span>
        </button>
      </div>

      {/* Right side - Export + AI Video + Record unified group */}
      <div className="ideaweave-canvas-header__right">
        <button
          type="button"
          className="ideaweave-canvas-header__export-btn"
          onClick={onExport}
          title={t("canvas.export")}
        >
          <span className="material-symbols-outlined">ios_share</span>
        </button>
        <span className="ideaweave-canvas-header__divider" />
        <AIVideoButton
          hasCanvasElements={video.hasCanvasElements}
          isLoading={video.isLoading}
          elapsedSeconds={video.elapsedSeconds}
          onOpenVideoSettings={video.openVideoSettings}
        />
        <span className="ideaweave-canvas-header__divider" />
        <RecordingFeature
          video={{
            isVideoSettingsOpen: video.isVideoSettingsOpen,
            closeVideoSettings: video.closeVideoSettings,
            startGeneration: video.startGeneration,
            voiceSampleUrlBuilder: video.voiceSampleUrlBuilder,
            showToast: video.showToast,
          }}
        />
      </div>
    </>
  );
};

interface IdeaWeaveCanvasFooterProps {
  zoom: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onUndo: () => void;
  onRedo: () => void;
}

export const IdeaWeaveCanvasFooter: React.FC<IdeaWeaveCanvasFooterProps> = ({
  zoom,
  onZoomIn,
  onZoomOut,
  onUndo,
  onRedo,
}) => {
  const { t } = useAppI18n();

  return (
    <div className="ideaweave-canvas-footer">
      {/* Undo/Redo controls */}
      <div className="ideaweave-canvas-footer__group">
        <button
          className="ideaweave-canvas-footer__btn"
          onClick={onUndo}
          title={t("canvas.undo")}
        >
          <span className="material-symbols-outlined">undo</span>
        </button>
        <button
          className="ideaweave-canvas-footer__btn"
          onClick={onRedo}
          title={t("canvas.redo")}
        >
          <span className="material-symbols-outlined">redo</span>
        </button>
      </div>

      {/* Zoom controls */}
      <div className="ideaweave-canvas-footer__group">
        <button
          className="ideaweave-canvas-footer__btn"
          onClick={onZoomOut}
          title={t("canvas.zoomOut")}
        >
          <span className="material-symbols-outlined">remove</span>
        </button>
        <span className="ideaweave-canvas-footer__zoom">
          {Math.round(zoom * 100)}%
        </span>
        <button
          className="ideaweave-canvas-footer__btn"
          onClick={onZoomIn}
          title={t("canvas.zoomIn")}
        >
          <span className="material-symbols-outlined">add</span>
        </button>
      </div>
    </div>
  );
};
